import {useNavigate} from "react-router-dom";
import {Alert, Box, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import * as React from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import InternalNotes from "./InternalNotes";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import Property from "crqlar-react-common/dist/components/Property";
import ServiceInfoRow from "crqlar-react-common/dist/components/ServiceInfoRow";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import ProfileInfoRow from "crqlar-react-common/dist/components/ProfileInfoRow";
import ResourceInfoRow from "crqlar-react-common/dist/components/ResourceInfoRow";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useService, useServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import ExternalInfoPreview from "../common/ExternalInfoPreview";
import ReservationGroupSimplePreview from "../reservationGroup/ReservationGroupSimplePreview";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {useQuery} from "react-query";
import {ReservationGroupService} from "crqlar-react-common/dist/services/reservation/reservationGroup.service";
import ReservationStatusSelect from "../../reservations/ReservationStatusSelect";

interface Props {
    reservationData: UseEntityData<Reservation>
}

const ReservationDetailsForm = ({
                                    reservationData
                                }: Props) => {

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const allAgeCategories = useAllAgeCategories()

    const reservation = reservationData.item
    const managedExternally = !!reservation.externalInfo?.[0]?.provider
    const setReservation = reservationData.setItem

    const resources = useResources(reservation.resourceIds || [])

    const reservationService = useService(reservation.serviceId)
    const serviceAgeCategories = allAgeCategories.filter(ac => reservationService?.ageCategoryIds?.includes(ac.id!))

    const rateServices = useServices(reservation.ratePlanSnaps?.map(rps => rps.serviceId!) || [])

    //endregion Sate

    //region Handlers

    const reservationGroupQuery = useQuery(['reservationGroup', reservation?.reservationGroupId], async () => {
        if (!reservation?.reservationGroupId) {
            return {}
        }

        return await ReservationGroupService.getReservationGroupById(reservation?.reservationGroupId)
    })
    const reservationGroup = reservationGroupQuery.data

    const updateField = (field: keyof Reservation, value: any) => {
        setReservation({...reservation, [field]: value})
    }

    //endregion Handlers

    //region UI

    const managedExternallySection = (
        managedExternally &&
        <Alert severity={'warning'}>
            {t('This entity is managed by an external system and thus cannot be edited here.', {source: reservation.externalInfo?.[0]?.provider})}
        </Alert>
    )

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const fieldGroupGap = {xs: 1.5, sm: 1}

    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    const hasAnyValue = (value: any): boolean => {
        if (Array.isArray(value)) {
            return value.some(hasAnyValue)
        }

        if (value && typeof value === 'object') {
            return Object.values(value).some(hasAnyValue)
        }

        return value !== undefined && value !== null && value !== ''
    }

    const displayedGuestCounts = (reservation.guestCounts && reservation.guestCounts.length > 0)
        ? reservation.guestCounts
        : serviceAgeCategories.map(ac => ({
            ageCategoryId: ac.id,
            count: 0
        }));

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>
            <Property
                title={t('Status')}
                tooltip={t('Current reservation lifecycle status. Externally managed reservations may not be editable here.')}
            >
                <ReservationStatusSelect
                    id={'status'}
                    value={reservation.status}
                    onChange={status => updateField('status', status)}
                    isExistingReservation={!!reservation.id}
                    fullWidth
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Reservation code/number')}
                tooltip={t('External or internal reservation identifier used for lookup and communication.')}
            >
                <TextField
                    id={'reservation-code'}
                    value={reservation.code || ''}
                    fullWidth
                    size={'small'}
                />
            </Property>

            <Property
                title={t('Start time (expected / actual)')}
                tooltip={t('Expected start is the planned schedule; actual start records when the reservation really began.')}
            >
                <Stack
                    width={'100%'}
                    direction={'row'}
                    gap={2}
                >
                    <Box
                        width={'50%'}
                    >
                        <CDatePicker
                            id={'expected-start-date'}
                            value={reservation.expectedStartDate}
                            onChange={e => {
                                updateField('expectedStartDate', e)
                            }}
                            disabled={managedExternally}
                        />
                    </Box>

                    <Box
                        width={'50%'}
                    >
                        <CDatePicker
                            id={'actual-start-date'}
                            value={reservation.actualStartDate}
                            onChange={e => {
                                updateField('actualStartDate', e)
                            }}
                            disabled={managedExternally}
                        />

                    </Box>
                </Stack>

            </Property>

            <Property
                title={t('End time (expected / actual)')}
                tooltip={t('Expected end is the planned schedule; actual end records when the reservation really finished.')}
            >
                <Stack
                    width={'100%'}
                    direction={'row'}
                    gap={2}
                >
                    <Box
                        width={'50%'}
                    >
                        <CDatePicker
                            id={'expected-end-date'}
                            value={reservation.expectedEndDate}
                            onChange={e => {
                                updateField('expectedEndDate', e)
                            }}
                            disabled={managedExternally}
                        />
                    </Box>

                    <Box
                        width={'50%'}
                    >
                        <CDatePicker
                            id={'actual-end-date'}
                            value={reservation.actualEndDate}
                            onChange={e => {
                                updateField('actualEndDate', e)
                            }}
                            disabled={managedExternally}
                        />

                    </Box>
                </Stack>

            </Property>

            <Property
                title={t('Cancellation time / reason')}
                tooltip={t('Cancellation timestamp and the reason recorded for the cancelled reservation.')}
            >
                <Stack
                    width={'100%'}
                    direction={'row'}
                    gap={2}
                >
                    <Box
                        width={'50%'}
                    >
                        <CDatePicker
                            id={'cancelled-date'}
                            value={reservation.cancelledDate}
                            onChange={e => {
                                updateField('cancelledDate', e)
                            }}
                            disabled={managedExternally}
                        />
                    </Box>

                    <TextField
                        id={'cancellation-reason'}
                        value={reservation.cancellationReason || ''}
                        onChange={(e) => {
                            updateField('cancellationReason', e.target.value)
                        }}
                        sx={{
                            width: '50%'
                        }}
                        size={'small'}
                        disabled={managedExternally}
                    />

                </Stack>

            </Property>

            <Property
                title={t('Deadline')}
                tooltip={t('Latest relevant deadline for this reservation, such as confirmation or cancellation cutoff.')}
            >
                <CDatePicker
                    id={'deadline-date'}
                    value={reservation.deadlineDate}
                    onChange={e => {
                        updateField('deadlineDate', e)
                    }}
                    disabled={managedExternally}
                />
            </Property>

            {
                displayedGuestCounts.map(guestCount => {
                    const count = guestCount.count || 0;
                    const ageCategory = allAgeCategories.find(ac => ac.id === guestCount.ageCategoryId);

                    return (
                        <Property title={ageCategory?.name || ''} key={ageCategory?.id}>
                            <CountTextField
                                value={count}
                                onChangeValue={e => {
                                    const updatedGuestCounts = (reservation.guestCounts || serviceAgeCategories.map(ac => ({
                                        ageCategoryId: ac.id,
                                        count: 0,
                                    }))).map(g =>
                                        g.ageCategoryId === ageCategory?.id
                                            ? {...g, count: e}
                                            : g
                                    );

                                    setReservation({
                                        ...reservation,
                                        guestCounts: updatedGuestCounts
                                    });
                                }}
                                sx={{
                                    width: '100%',
                                }}
                                fullWidth
                                size={'small'}
                                disabled={!ageCategory || managedExternally}
                                data-test-id={'guest-count'}
                            />
                        </Property>
                    );
                })
            }

            </Stack>
        </Stack>
    )

    const serviceSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Service')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <ServiceInfoRow
                    id={'service-find'}
                    serviceId={reservation.serviceId}
                    setServiceId={(serviceId) => {
                        updateField('serviceId', serviceId)
                    }}
                    onDelete={() => {
                        updateField('serviceId', undefined)
                    }}
                    disabled={managedExternally}
                />
            </Stack>
        </Stack>
    )


    const profilesSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Profiles')}
            </Typography>

            <Stack gap={fieldGroupGap}>
            {
                reservation.profileIds?.map((profileId, i) => {
                    const relatedProfiles = reservationGroup?.relatedProfileIds?.[profileId] || []

                    return (
                        <Stack gap={1} key={profileId || i}>
                            <ProfileInfoRow
                                id={'profile'}
                                profileId={profileId}
                                setProfileId={(profileId) => {
                                    if (profileId) {
                                        const newProfileIds = [...(reservation.profileIds || [])]
                                        newProfileIds[i] = profileId
                                        updateField('profileIds', newProfileIds)
                                    }
                                }}
                                onDelete={() => {
                                    const newProfileIds = [...(reservation.profileIds || [])]
                                    newProfileIds.splice(i, 1)
                                    updateField('profileIds', newProfileIds)
                                }}
                                isPrimary={profileId === reservation.primaryProfileId}
                                disabled={managedExternally}
                            />

                            {
                                !!relatedProfiles.length &&
                                <Stack
                                    sx={{
                                        direction: 'row',
                                        alignItems: 'center',
                                        pl: 2
                                    }}
                                >
                                    {
                                        relatedProfiles.map((relatedProfile, i) => (
                                            <ProfileInfoRow
                                                key={relatedProfile || i}
                                                profileId={relatedProfile}
                                                data-testid={`related-profile-${i}`}
                                                title={t('Related profile')}
                                                disabled={managedExternally}
                                            />
                                        ))
                                    }
                                </Stack>
                            }

                        </Stack>
                    )
                })
            }

            {
                !managedExternally &&
                <Button
                    id={'btn-add-profile'}
                    variant={'text'}
                    onClick={() => {
                        updateField('profileIds', [...(reservation.profileIds || []), ''])
                    }}
                    startIcon={<AddIcon/>}
                    sx={{
                        alignSelf: 'flex-start'
                    }}
                >
                    {t('Add profile')}
                </Button>
            }

            {
                reservation.reservationRequestData &&
                <Property
                    title={t('Reservation request data')}
                    tooltip={t('Original contact details submitted with the reservation request.')}
                >
                    <Stack
                        sx={{
                            background: '#eaeaea',
                            p: 1
                        }}
                    >
                        <Typography>
                            {t('First name')}: {reservation.reservationRequestData?.firstName}
                        </Typography>
                        <Typography>
                            {t('Last name')}: {reservation.reservationRequestData?.lastName}
                        </Typography>
                        <Typography>
                            {t('Email')}: {reservation.reservationRequestData?.email}
                        </Typography>
                        <Typography>
                            {t('Phone')}: {reservation.reservationRequestData?.phone}
                        </Typography>

                    </Stack>
                </Property>
            }

            </Stack>
        </Stack>
    )

    const resourcesSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Resources')}
            </Typography>

            <Stack gap={fieldGroupGap}>
            {
                reservation.resourceIds?.map((resourceId, i) => {
                        return (
                            <ResourceInfoRow
                                key={resourceId || i}
                                resourceId={resourceId}
                                setResourceId={(resourceId) => {
                                    if (resourceId) {
                                        const newResourceIds = [...(reservation.resourceIds || [])]
                                        newResourceIds[i] = resourceId
                                        updateField('resourceIds', newResourceIds)
                                    }
                                }}
                                onDelete={() => {
                                    const newResourceIds = [...(reservation.resourceIds || [])]
                                    newResourceIds.splice(i, 1)
                                    updateField('resourceIds', newResourceIds)
                                }}
                                disabled={managedExternally}
                            />
                        )
                    }
                )
            }

            {
                !managedExternally &&
                <Button
                    id={'btn-add-resource'}
                    variant={'text'}
                    onClick={() => {
                        updateField('resourceIds', [...(reservation.resourceIds || []), ''])
                    }}
                    startIcon={<AddIcon/>}
                    sx={{
                        alignSelf: 'flex-start'
                    }}
                >
                    {t('Add resource')}
                </Button>
            }

            </Stack>
        </Stack>
    )

    const hasExternalInfo = !!reservation.externalInfo?.length
    const hasAdditionalReservationFields = hasAnyValue(reservation.additionalInfo)
    const hasResourcesAdditionalData = !!reservation.resourceAdditionalInfo?.some(hasAnyValue)
    const hasRatePlanSnaps = !!reservation.ratePlanSnaps?.length
    const hasRateSnaps = !!reservation.rateSnaps?.length
    const hasAdditionalData = hasExternalInfo || hasAdditionalReservationFields || hasResourcesAdditionalData || hasRatePlanSnaps || hasRateSnaps
    const hasReservationGroup = !!reservation.reservationGroupId

    const resourcesAdditionalData = hasResourcesAdditionalData && (
        <AdditionalProperties title={t('Resources additional data')} variant={'outlined'}>

            {
                reservation.resourceAdditionalInfo?.map((resourceInfo, i) => {

                    const resource = resources.find(r => r.id === resourceInfo.resourceId)

                    return (
                        <Property
                            key={resource?.id}
                            title={resource?.name || i.toString()}
                            tooltip={t('Additional data stored for this assigned resource.')}
                        >
                            <Stack>
                                {
                                    Object.entries(resourceInfo).map(entry => {
                                        return (
                                            <Typography
                                                key={entry[0]} data-testid={`resource-additional-info-${entry[0]}`}
                                            >
                                                {entry[0]}: {JSON.stringify(entry[1], null, 2)}
                                            </Typography>
                                        )
                                    })
                                }
                            </Stack>
                        </Property>
                    )
                })
            }

        </AdditionalProperties>
    )

    const additionalReservationFields = hasAdditionalReservationFields && (
        <AdditionalProperties title={t('Extension data')} variant={'outlined'}>
            <Property
                title={t('Market code')}
                tooltip={t('External market or reporting code stored with this reservation.')}
            >
                <TextField
                    id={'market-code'}
                    data-testid={'market-code'}
                    value={reservation.additionalInfo?.marketCode || ''}
                    fullWidth
                    size={'small'}
                    onChange={(e) => {
                        updateField('additionalInfo', {
                            ...reservation.additionalInfo,
                            marketCode: e.target.value
                        })
                    }}
                    disabled={managedExternally}
                />
            </Property>

            <Divider/>

            <Property
                title={t('Source of business')}
                tooltip={t('External source-of-business value imported or stored for this reservation.')}
            >
                <TextField
                    id={'source-of-business'}
                    data-testid={'source-of-business'}
                    value={reservation.additionalInfo?.sourceOfBusiness || ''}
                    fullWidth
                    size={'small'}
                    onChange={(e) => {
                        updateField('additionalInfo', {
                            ...reservation.additionalInfo,
                            sourceOfBusiness: e.target.value
                        })
                    }}
                    disabled={managedExternally}
                />
            </Property>

            <Divider/>

            <Property
                title={t('Status code (original)')}
                tooltip={t('Original status code received from the connected external system.')}
            >
                <TextField
                    id={'status-code-original'}
                    data-testid={'status-code-original'}
                    value={reservation.additionalInfo?.statusOriginalCode || ''}
                    fullWidth
                    size={'small'}
                    onChange={(e) => {
                        updateField('additionalInfo', {
                            ...reservation.additionalInfo,
                            statusOriginalCode: e.target.value
                        })
                    }}
                    disabled={managedExternally}
                />
            </Property>

            <Divider/>

            <Property
                title={t('Status name (original)')}
                tooltip={t('Original status name received from the connected external system.')}
            >
                <TextField
                    id={'status-original-name'}
                    data-testid={'status-original-name'}
                    value={reservation.additionalInfo?.statusOriginalName || ''}
                    fullWidth
                    size={'small'}
                    onChange={(e) => {
                        updateField('additionalInfo', {
                            ...reservation.additionalInfo,
                            statusOriginalName: e.target.value
                        })
                    }}
                    disabled={managedExternally}
                />
            </Property>

            <Divider/>

            <Property
                title={t('Communication channel')}
                tooltip={t('Channel through which this reservation was created or communicated.')}
            >
                <TextField
                    id={'communication-channel'}
                    data-testid={'communication-channel'}
                    value={reservation.additionalInfo?.communicationChannel || ''}
                    fullWidth
                    size={'small'}
                    onChange={(e) => {
                        updateField('additionalInfo', {
                            ...reservation.additionalInfo,
                            communicationChannel: e.target.value
                        })
                    }}
                    disabled={managedExternally}
                />
            </Property>

            <Divider/>

            <Property
                title={t('Travel reason')}
                tooltip={t('Travel reason imported or stored for accommodation-style reservations.')}
            >
                <TextField
                    id={'travel-reason'}
                    data-testid={'travel-reason'}
                    value={reservation.additionalInfo?.travelReason || ''}
                    fullWidth
                    size={'small'}
                    onChange={(e) => {
                        updateField('additionalInfo', {
                            ...reservation.additionalInfo,
                            travelReason: e.target.value
                        })
                    }}
                    disabled={managedExternally}
                />
            </Property>

            <Divider/>

            <Property
                title={t('Sharers')}
                tooltip={t('Linked shared-stay reservations imported from the source system.')}
            >
                <Stack>
                    {
                        reservation.additionalInfo?.sharers?.map((sharer, i) => {
                            return (
                                <Stack key={i} direction={'row'} gap={2}>
                                    <Typography data-testid={`sharer-${i}`}>
                                        {t("Reservation id")}: {sharer.reservationId}
                                    </Typography>
                                    <Typography data-testid={`sharer-main-${i}`}>
                                        {sharer.isMainSharer ? t('Main sharer') : ''}
                                    </Typography>
                                </Stack>
                            )
                        })
                    }
                </Stack>
            </Property>

        </AdditionalProperties>
    )

    const ratePlanSnaps = hasRatePlanSnaps && (
        <AdditionalProperties title={t('Rate plans')} variant={'outlined'}>

            {
                reservation.ratePlanSnaps?.map((info, i) => {
                    const service = rateServices.find(s => s.id === info.serviceId)
                    return (
                        <Property
                            key={service?.name}
                            title={service?.name || i.toString()}
                            tooltip={t('Snapshot of the rate-plan data captured for this reservation.')}
                        >
                            <Stack>
                                <Typography data-testid={`rate-plan-service-${i}`}>
                                    {t('Service')}: {service?.name} ({service?.marketCode})
                                </Typography>
                                <Typography data-testid={`rate-plan-from-${i}`}>
                                    {t('From')}: {info.startDay}
                                </Typography>
                                <Typography data-testid={`rate-plan-to-${i}`}>
                                    {t('To')}: {info.endDay}
                                </Typography>
                                <Typography data-testid={`rate-plan-price-viewable-${i}`}>
                                    {t('Price viewable')}: {info.priceViewable ? t('Yes') : t('No')}
                                </Typography>
                            </Stack>
                        </Property>
                    )
                })
            }

        </AdditionalProperties>
    )

    const rateSnaps = hasRateSnaps && (
        <AdditionalProperties title={t('Rates')} variant={'outlined'}>

            {
                reservation.rateSnaps?.map((info, i) => {

                    return (
                        <Property
                            key={info.rateId}
                            title={info.name || i.toString()}
                            tooltip={t('Snapshot of the rate data captured for this reservation.')}
                        >
                            <Stack>
                                <Typography data-testid={`rate-snap-service-${i}`}>
                                    {t('Rate plan code')}: {info.ratePlanCode}
                                </Typography>
                                <Typography data-testid={`rate-snap-from-${i}`}>
                                    {t('From')}: {info.startDay}
                                </Typography>
                                <Typography data-testid={`rate-snap-to-${i}`}>
                                    {t('To')}: {info.endDay}
                                </Typography>
                                <Typography data-testid={`rate-snap-price-${i}`}>
                                    {t('Price')}: {info.price?.amount} {info.price?.currency}
                                </Typography>

                                {
                                    info.price?.amountBeforeTax &&
                                    <Typography data-testid={`rate-snap-price-before-tax-${i}`}>
                                        {t('Price before tax')}: {info.price?.amountBeforeTax} {info.price?.currency}
                                    </Typography>
                                }

                                <Typography data-testid={`rate-snap-inventory-block-code-${i}`}>
                                    {t('Inventory block code')}: {info.invBlockCode}
                                </Typography>

                                {
                                    info.resourceId &&
                                    <Typography data-testid={`rate-snap-resource-${i}`}>
                                        {t('Resource')}: {info.resourceName}
                                    </Typography>
                                }

                                {
                                    !!info.notes?.length &&
                                    <Stack>
                                        <Typography variant={'h6'}>{t('Notes')}</Typography>
                                        {
                                            info.notes.map(note => {
                                                return (
                                                    <Typography sx={{ml: 2}} data-testid={`rate-snap-note-${i}`}>
                                                        {note.name}: {note.note}
                                                    </Typography>
                                                )
                                            })
                                        }
                                    </Stack>
                                }

                                {
                                    !!info.guestCounts?.length &&
                                    <Stack>
                                        <Typography variant={'h6'}>{t('Guest count')}</Typography>
                                        {
                                            info.guestCounts.map(count => {
                                                return (
                                                    <Typography sx={{ml: 2}} data-testid={`rate-snap-guest-count-${i}`}>
                                                        {allAgeCategories.find(ageCategory => ageCategory.id === count.ageCategoryId)?.name}: {count.count}
                                                    </Typography>
                                                )
                                            })
                                        }
                                    </Stack>
                                }

                                {
                                    !!info.rateAmounts?.length &&
                                    <Stack direction={'row'} flexWrap={'wrap'} gap={1}>
                                        {
                                            info.rateAmounts?.map((rateAmount, i) => {
                                                return (
                                                    <Stack
                                                        key={i}
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            padding: 1,
                                                            mt: 1,
                                                            width: '40%'
                                                        }}
                                                    >
                                                        <Typography data-testid={`rate-snap-category-${i}`}>
                                                            {t('Category')}: {rateAmount.itemCategory}
                                                        </Typography>
                                                        <Typography data-testid={`rate-snap-amount-${i}`}>
                                                            {t('Amount')}: {rateAmount.amount?.amount} {rateAmount.amount?.currency}
                                                        </Typography>
                                                        <Typography data-testid={`rate-snap-amount-before-tax-${i}`}>
                                                            {t('Amount before tax')}: {rateAmount.amount?.amountBeforeTax} {rateAmount.amount?.currency}
                                                        </Typography>
                                                        <Typography data-testid={`rate-snap-fixed-charge-${i}`}>
                                                            {t('Fixed charge')}: {rateAmount.isFixedCharge ? t('Yes') : t('No')}
                                                        </Typography>
                                                    </Stack>
                                                )
                                            })
                                        }
                                    </Stack>
                                }

                                <Divider sx={{mt: 1, mb: 2}}/>

                            </Stack>
                        </Property>
                    )
                })
            }

        </AdditionalProperties>
    )

    const notesSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Notes')}
            </Typography>

            <InternalNotes
                data={reservationData}
                disabled={managedExternally}
            />

        </Stack>
    )

    const additionalDataSection = hasAdditionalData && (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Additional reservation data')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <ExternalInfoPreview externalInfo={reservation.externalInfo}/>
                {additionalReservationFields}
                {resourcesAdditionalData}
                {ratePlanSnaps}
                {rateSnaps}
            </Stack>
        </Stack>
    )

    const reservationGroupSection = hasReservationGroup && (
        <Stack
            sx={{
                gap: {xs: 2.5, sm: 2},
            }}
        >
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Reservation group')}
            </Typography>

            <ReservationGroupSimplePreview
                id={reservation.reservationGroupId}
                reservationId={reservation.id}
                onOpenReservation={(r) => {
                    navigate(`/system/reservations/${r.id}`)
                }}
            />
        </Stack>
    )

    //endregion UI

    const reservationSections = [
        {
            key: 'basic-info',
            content: basicInfoSection,
        },
        {
            key: 'service',
            content: serviceSection,
        },
        {
            key: 'profiles',
            content: profilesSection,
        },
        {
            key: 'resources',
            content: resourcesSection,
        },
        {
            key: 'notes',
            content: notesSection,
        },
        {
            key: 'additional-data',
            content: additionalDataSection,
        },
        {
            key: 'reservation-group',
            content: reservationGroupSection,
        },
    ].filter(section => !!section.content)

    const reservationDataUI = (
        <Stack
            sx={{
                width: '100%',
                gap: 6
            }}
        >
            {managedExternallySection}
            {
                reservationSections.map((section, index) => (
                    <React.Fragment key={section.key}>
                        {
                            index > 0 &&
                            <Divider sx={sectionDividerSx}/>
                        }

                        {section.content}
                    </React.Fragment>
                ))
            }

        </Stack>
    )

    return (
        <>
            {reservationDataUI}
        </>
    );
}

export default ReservationDetailsForm;
